import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import Swal from 'sweetalert2'
import * as Yup from 'yup'
import { setLocale } from 'yup'
import { ru } from 'yup-locales'
import { useFormik } from 'formik'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { ROLE, useAuth } from '../../../auth'
import { IsGranted } from '../../../auth/PrivateRoute'
import { ClientEditRequest, EClientFlags, IClientItem } from '../../core/_models'
import {
  changeMatchingPayoutRestriction,
  editClientByToken,
  getClientByToken,
} from '../../core/_requests'
import { CLIENTS_PAGE_BREADCRUMBS } from '../../ClientsPage.consts'
import { convertUTCtoMSK } from '../../../../../utils'
import { ChangeRiskScoreModal } from './ChangeRiskScoreModal'
import { ChangeTemporaryRiskScoreModal } from './ChangeTemporaryRiskScoreModal'
import { ClientRouting } from './components'
import { getErrorText, getFieldErrors } from '../../../../common/api/errors'

setLocale(ru)

const breadCrumbs = [...CLIENTS_PAGE_BREADCRUMBS]
const COMMENT_MAX_LENGTH = 1000
const clientSchema = Yup.object().shape({
  color: Yup.string().required('Обязательное поле'),
  comment: Yup.string().max(COMMENT_MAX_LENGTH, `Не более ${COMMENT_MAX_LENGTH} символов`),
})

const defaultClientEdit: ClientEditRequest = {
  color: '',
  comment: '',
}

const errorStyle = { color: 'red' }

export const EditClient: React.FC = () => {
  const { token } = useParams()
  const { currentUser } = useAuth()
  const intl = useIntl()

  const [client, setClient] = useState(defaultClientEdit)
  const [loading, setLoading] = useState(false)
  const [errorAPI, setErrorAPI] = useState('')
  const [riskPoints, setRiskPoints] = useState<number | undefined>(undefined)
  const [manualRiskPointChange, setManualRiskPointChange] = useState<number>(0)
  const [manualRiskPendingChange, setManualRiskPendingChange] = useState<number | null>(null)
  const [manualRiskExpiresAt, setManualRiskExpiresAt] = useState<string | null>(null)
  const [manualRiskReason, setManualRiskReason] = useState<string | null>(null)
  const [matchingPayoutRestricted, setMatchingPayoutRestricted] = useState(false)
  const [restrictionSaving, setRestrictionSaving] = useState(false)
  const [showRiskScoreModal, setShowRiskScoreModal] = useState(false)
  const [showTemporaryRiskScoreModal, setShowTemporaryRiskScoreModal] = useState(false)

  // CPAYMENT-1498: блок маршрутизации — только менеджер ПС (админ по иерархии) и менеджер
  // мерчантов; тот же набор ролей, что у эндпоинта /api/internal/external_user/routing.
  const canSeeRouting = IsGranted([ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser)

  breadCrumbs.forEach((value, index) => {
    if (value.title === 'Настройки') {
      breadCrumbs[index].isActive = true
    }
    breadCrumbs[index].path = breadCrumbs[index].path.replace('__token__', token + '')
  })

  let initialValues: ClientEditRequest = Object.assign(client, {})

  useEffect(() => {
    getClientInfo()
  }, [])

  let formik = useFormik<ClientEditRequest>({
    initialValues,
    validationSchema: clientSchema,
    onSubmit: (values) => {
      setLoading(true)
      saveClient(values)
    },
    enableReinitialize: true,
  })

  const applyRiskState = (externalUser: IClientItem) => {
    setRiskPoints(externalUser.riskPoints)
    setManualRiskPointChange(externalUser.manualRiskPointChange || 0)
    setManualRiskPendingChange(externalUser.manualRiskPendingChange ?? null)
    setManualRiskExpiresAt(externalUser.manualRiskExpiresAt ?? null)
    setManualRiskReason(externalUser.manualRiskReason ?? null)
    setMatchingPayoutRestricted(externalUser.matchingPayoutRestricted ?? false)
  }

  /**
   * Запрет выплат в мэтчинг сохраняется сразу, отдельной ручкой: он не часть формы клиента и
   * кнопку «Сохранить» не ждёт. Состояние берём из ответа, а не из клика, — источником остаётся
   * сервер.
   */
  const toggleMatchingPayoutRestriction = async (restricted: boolean) => {
    setRestrictionSaving(true)

    try {
      const response = await changeMatchingPayoutRestriction(token + '', { restricted })

      if (response.data.success && response.data.externalUser) {
        applyRiskState(response.data.externalUser)
        setErrorAPI('')
      } else {
        setErrorAPI(
          'Возникла ошибка: ' +
            (response.data.errors ? response.data.errors.general_error : 'запрет выплат не изменён')
        )
      }
    } catch (e) {
      setErrorAPI('Возникла ошибка: запрет выплат в мэтчинг не изменён')
    } finally {
      setRestrictionSaving(false)
    }
  }

  // Бессрочная часть отдельной колонки не имеет: она равна ручной части минус временная.
  const permanentRiskChange = manualRiskPointChange - (manualRiskPendingChange ?? 0)

  const getClientInfo = async () => {
    let response
    try {
      response = await getClientByToken(token + '', currentUser)
    } catch (e) {
      setErrorAPI('Возникла ошибка: ' + getErrorText(e, 'не удалось загрузить клиента', { withFieldName: false }))

      return false
    }

    if (!response.data) {
      return false
    }

    if (response.data.success && response.data.externalUser) {
      setClient({
        color: response.data.externalUser.options.color,
        comment: response.data.externalUser.comment ? response.data.externalUser.comment : '',
      })
      applyRiskState(response.data.externalUser)
    }
  }

  const saveClient = async (values: ClientEditRequest) => {
    let copyUpdateValues = Object.assign({}, values)
    let response
    try {
      response = await editClientByToken(token + '', copyUpdateValues)
    } catch (error: unknown) {
      // The interceptor has shown the refusal; release the form and mark the fields a validation error names.
      Object.entries(getFieldErrors(error)).forEach(([key, value]) => formik.setFieldError(key, value))
      return
    } finally {
      setLoading(false)
    }

    if (response.data.success && response.data.externalUser) {
      Swal.fire({
        title: 'Клиент успешно изменён',
        icon: 'success',
        timer: 1500,
      })

      setClient({
        color: response.data.externalUser.options.color,
        comment: response.data.externalUser.comment ? response.data.externalUser.comment : '',
      })
      applyRiskState(response.data.externalUser)
      setErrorAPI('')
    } else if (response.data.errors) {
      Object.entries(response.data.errors).forEach(([key, value]) => {
        if (key === 'general_error') {
          setErrorAPI('Возникла ошибка: ' + value)
        } else {
          formik.setFieldError(key, value)
        }
      })
    }
  }

  // Расчет итогового риск-балла
  const calculateTotalRiskPoints = () => {
    const basePoints = riskPoints || 0
    return basePoints + manualRiskPointChange
  }

  return (
    <>
    <div className='card mb-5 mb-xl-10'>
      <PageTitle breadcrumbs={breadCrumbs}>Редактирование клиента</PageTitle>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_profile_details'
        aria-expanded='true'
        aria-controls='kt_account_profile_details'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Редактирование клиента</h3>
        </div>
      </div>

      <div id='kt_account_profile_details' className='collapse show'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>Флаг</label>

              <div className='col-lg-8 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg fw-bold'
                  {...formik.getFieldProps('color')}
                >
                  {Object.values(EClientFlags).map((value, index) => (
                    <option value={value} key={index}>
                      {intl.formatMessage({ id: 'client.flag.' + value })}
                    </option>
                  ))}
                </select>
                {formik.touched.color && formik.errors.color && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.color}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Комментарий</label>

              <div className='col-lg-8 fv-row'>
                <textarea
                  rows={4}
                  maxLength={COMMENT_MAX_LENGTH}
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Комментарий'
                  {...formik.getFieldProps('comment')}
                />
                {formik.touched.comment && formik.errors.comment && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.comment}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Блок Риск балл */}
            <div className='separator separator-dashed my-6'></div>
            <h3 className='card-title fw-bold fs-3 m-0 mb-5'>Риск балл</h3>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Автоматический расчет</label>
              <div className='col-lg-8 fv-row'>
                <span className='fw-bold fs-6'>{riskPoints !== undefined ? riskPoints : 'Загрузка...'}</span>
              </div>
            </div>

            {/*
              Две корректировки — две строки со своей кнопкой у каждой. Одна кнопка на обе
              настройки и одно поле, управлявшее и значением, и сроком, — то, что QA назвало
              неинтуитивным.
            */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Бессрочная корректировка</label>
              <div className='col-lg-8 fv-row d-flex align-items-center gap-3'>
                <span className='fw-bold fs-6'>{permanentRiskChange}</span>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={() => setShowRiskScoreModal(true)}
                >
                  Изменить
                </button>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Временная корректировка</label>
              <div className='col-lg-8 fv-row'>
                <div className='d-flex align-items-center gap-3'>
                  <span className='fw-bold fs-6'>
                    {manualRiskPendingChange === null ? (
                      'Нет'
                    ) : (
                      <>
                        {manualRiskPendingChange > 0 ? '+' : ''}
                        {manualRiskPendingChange}
                        {manualRiskExpiresAt ? ` до ${convertUTCtoMSK(manualRiskExpiresAt)}` : ''}
                      </>
                    )}
                  </span>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => setShowTemporaryRiskScoreModal(true)}
                  >
                    Изменить
                  </button>
                </div>
                {manualRiskPendingChange !== null && (
                  <div className='form-text text-muted'>
                    {manualRiskReason ? `${manualRiskReason}. ` : ''}
                    По истечении срока система снимет эту корректировку автоматически
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Итоговый риск балл</label>
              <div className='col-lg-8 fv-row'>
                <span className='fw-bold fs-6'>{calculateTotalRiskPoints()}</span>
              </div>
            </div>

            {/*
              Запрет выплат стоит в этом блоке, но баллу не подчиняется: балл описывает риск
              плательщика на депозите и выплату не режет. Кто из клиентов с высоким баллом не
              должен получать выплаты в мэтчинге — решает сотрудник, тумблером.
            */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                Запрет выплат в мэтчинг
              </label>
              <div className='col-lg-8 fv-row'>
                <div className='d-flex align-items-center gap-3'>
                  <div className='form-check form-switch form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='matchingPayoutRestricted'
                      checked={matchingPayoutRestricted}
                      disabled={restrictionSaving}
                      onChange={(event) => toggleMatchingPayoutRestriction(event.target.checked)}
                    />
                  </div>
                  {restrictionSaving && (
                    <span className='text-muted fs-7'>
                      Сохраняем...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </div>
                <div className='form-text text-muted'>
                  Новые выплаты этого клиента в мэтчинг не принимаются и уходят следующему
                  адаптеру. Пополнений и уже принятых выплат запрет не касается.
                </div>
              </div>
            </div>

          </div>

          {errorAPI ? (
            <div className='row mb-6 fw-bold fs-6 border-top p-9' style={errorStyle}>
              {errorAPI}{' '}
            </div>
          ) : (
            ''
          )}
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button type='submit' className='btn btn-primary' disabled={loading}>
              {!loading && 'Сохранить'}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Идёт сохранение...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      <ChangeRiskScoreModal
        show={showRiskScoreModal}
        token={token + ''}
        riskPoints={riskPoints || 0}
        manualChange={manualRiskPointChange}
        permanentChange={permanentRiskChange}
        onClose={() => setShowRiskScoreModal(false)}
        onSave={(externalUser) => {
          applyRiskState(externalUser)
          setShowRiskScoreModal(false)
        }}
      />

      <ChangeTemporaryRiskScoreModal
        show={showTemporaryRiskScoreModal}
        token={token + ''}
        riskPoints={riskPoints || 0}
        manualChange={manualRiskPointChange}
        pendingChange={manualRiskPendingChange}
        expiresAt={manualRiskExpiresAt}
        currentReason={manualRiskReason}
        onClose={() => setShowTemporaryRiskScoreModal(false)}
        onSave={(externalUser) => {
          applyRiskState(externalUser)
          setShowTemporaryRiskScoreModal(false)
        }}
      />
    </div>

    {/* Read-only витрина: намеренно ОТДЕЛЬНОЙ карточкой, а не внутри <form> с кнопкой «Сохранить». */}
    {canSeeRouting && (
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Маршрутизация</h3>
          </div>
        </div>
        <div className='card-body border-top p-9'>
          <ClientRouting token={token + ''} />
        </div>
      </div>
    )}
    </>
  )
}
